/**
 * Enhanced IndexedDB Adapter
 * Improved IndexedDB adapter with event integration, better error handling, and performance optimizations
 */

import { StorageAdapter, StorageResult, StorageOptions } from '../types'
import { softDeleteVia, restoreVia, listTrashVia, type TrashPrimitives } from '../trash';

export class IndexedDBAdapter implements StorageAdapter {
  private dbName: string
  private version: number
  private db: IDBDatabase | null = null
  private eventEmitter: any // Will be injected by the storage registry
  private isInitialized: boolean = false

  constructor(dbName: string = 'freshcards', version: number = 1, eventEmitter?: any) {
    this.dbName = dbName
    this.version = version
    this.eventEmitter = eventEmitter
  }

  async initialize(): Promise<void> {
    if (this.isInitialized) return

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version)
      
      request.onerror = () => {
        console.error(`[IndexedDB] Failed to open database: ${this.dbName}`)
        reject(new Error('Failed to open IndexedDB'))
      }
      
      request.onsuccess = () => {
        this.db = request.result
        this.isInitialized = true
        console.log(`[IndexedDB] Database initialized: ${this.dbName}`)
        
        // Emit initialization event
        if (this.eventEmitter) {
          this.eventEmitter.emit('storage:initialized', {
            adapter: this.getName(),
            database: this.dbName
          })
        }
        
        resolve()
      }
      
      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result
        
        // Create object store if it doesn't exist
        if (!db.objectStoreNames.contains('freshcards')) {
          const store = db.createObjectStore('freshcards', { keyPath: 'key' })
          // Create indexes for better performance
          store.createIndex('byKey', 'key', { unique: true })
          store.createIndex('byTimestamp', 'timestamp')
        }
        
        // Handle version upgrades
        const oldVersion = event.oldVersion
        if (oldVersion < 1) {
          // Initial setup
        }
      }
    })
  }

  private ensureDb(): IDBDatabase {
    if (!this.db || !this.isInitialized) {
      throw new Error('IndexedDB not initialized. Call initialize() first.')
    }
    return this.db
  }

  async save(key: string, value: any, options: StorageOptions = {}): Promise<void> {
    const startTime = Date.now()
    const db = this.ensureDb()
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['freshcards'], 'readwrite')
      const store = transaction.objectStore('freshcards')
      
      const entry = {
        key,
        value,
        timestamp: new Date(),
        adapter: this.getName()
      }
      
      const request = store.put(entry)
      
      request.onerror = () => {
        const error = new Error('Failed to save to IndexedDB')
        console.error(`[IndexedDB] Failed to save ${key}:`, error)
        
        // Emit error event
        if (this.eventEmitter) {
          this.eventEmitter.emit('storage:error', {
            error,
            operation: 'save',
            key,
            adapter: this.getName()
          })
        }
        
        reject(error)
      }
      
      request.onsuccess = () => {
        const duration = Date.now() - startTime
        
        // Emit event if enabled
        if (options.event !== false && this.eventEmitter) {
          this.eventEmitter.emit('storage:save', {
            key,
            data: value,
            adapter: this.getName(),
            timestamp: new Date(),
            size: JSON.stringify(value).length
          })
        }
        
        console.log(`[IndexedDB] Saved ${key} in ${duration}ms`)
        resolve()
      }
    })
  }

  async load(key: string, options: StorageOptions = {}): Promise<any> {
    const startTime = Date.now()
    const db = this.ensureDb()
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['freshcards'], 'readonly')
      const store = transaction.objectStore('freshcards')
      
      const request = store.get(key)
      
      request.onerror = () => {
        const error = new Error('Failed to load from IndexedDB')
        console.error(`[IndexedDB] Failed to load ${key}:`, error)
        
        // Emit error event
        if (this.eventEmitter) {
          this.eventEmitter.emit('storage:error', {
            error,
            operation: 'load',
            key,
            adapter: this.getName()
          })
        }
        
        reject(error)
      }
      
      request.onsuccess = () => {
        const duration = Date.now() - startTime
        const result = request.result
        
        if (!result) {
          console.log(`[IndexedDB] Key ${key} not found`)
          resolve(null)
          return
        }
        
        // Emit event if enabled
        if (options.event !== false && this.eventEmitter) {
          this.eventEmitter.emit('storage:load', {
            key,
            data: result.value,
            adapter: this.getName(),
            timestamp: new Date(),
            size: JSON.stringify(result.value).length
          })
        }
        
        console.log(`[IndexedDB] Loaded ${key} in ${duration}ms`)
        resolve(result.value)
      }
    })
  }

  async delete(key: string, options: StorageOptions = {}): Promise<void> {
    const db = this.ensureDb()
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['freshcards'], 'readwrite')
      const store = transaction.objectStore('freshcards')
      
      const request = store.delete(key)
      
      request.onerror = () => {
        const error = new Error('Failed to delete from IndexedDB')
        console.error(`[IndexedDB] Failed to delete ${key}:`, error)
        
        // Emit error event
        if (this.eventEmitter) {
          this.eventEmitter.emit('storage:error', {
            error,
            operation: 'delete',
            key,
            adapter: this.getName()
          })
        }
        
        reject(error)
      }
      
      request.onsuccess = () => {
        // Emit event if enabled
        if (options.event !== false && this.eventEmitter) {
          this.eventEmitter.emit('storage:delete', {
            key,
            adapter: this.getName(),
            timestamp: new Date(),
            existed: true
          })
        }
        
        console.log(`[IndexedDB] Deleted ${key}`)
        resolve()
      }
    })
  }

  async list(prefix: string = '', options: StorageOptions = {}): Promise<string[]> {
    const db = this.ensureDb()
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['freshcards'], 'readonly')
      const store = transaction.objectStore('freshcards')
      
      // Use index for better performance with prefix filtering
      const index = store.index('byKey')
      const range = IDBKeyRange.bound(prefix, prefix + '\uffff', false, false)
      
      const request = index.getAllKeys(range)
      
      request.onerror = () => {
        const error = new Error('Failed to list IndexedDB keys')
        console.error(`[IndexedDB] Failed to list keys with prefix '${prefix}':`, error)
        
        // Emit error event
        if (this.eventEmitter) {
          this.eventEmitter.emit('storage:error', {
            error,
            operation: 'list',
            adapter: this.getName()
          })
        }
        
        reject(error)
      }
      
      request.onsuccess = () => {
        const keys = request.result as string[]
        console.log(`[IndexedDB] Listed ${keys.length} keys with prefix '${prefix}'`)
        resolve(keys)
      }
    })
  }

  async export(): Promise<Blob> {
    const db = this.ensureDb()
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['freshcards'], 'readonly')
      const store = transaction.objectStore('freshcards')
      
      const request = store.getAll()
      
      request.onerror = () => {
        const error = new Error('Failed to export from IndexedDB')
        console.error('[IndexedDB] Failed to export:', error)
        
        // Emit error event
        if (this.eventEmitter) {
          this.eventEmitter.emit('storage:error', {
            error,
            operation: 'export',
            adapter: this.getName()
          })
        }
        
        reject(error)
      }
      
      request.onsuccess = () => {
        const items = request.result
        const data: { [key: string]: any } = {}
        let totalSize = 0
        
        items.forEach(item => {
          data[item.key] = item.value
          totalSize += JSON.stringify(item.value).length
        })
        
        const blob = new Blob([JSON.stringify(data, null, 2)], { 
          type: 'application/json' 
        })
        
        console.log(`[IndexedDB] Exported ${items.length} items (${totalSize} bytes)`)
        resolve(blob)
      }
    })
  }

  async import(data: Blob, options: StorageOptions = {}): Promise<void> {
    try {
      const text = await data.text()
      const importedData = JSON.parse(text)
      let importedCount = 0
      
      // Use batch operations for better performance
      const batchOperations: Promise<void>[] = []
      
      for (const [key, value] of Object.entries(importedData)) {
        batchOperations.push(this.save(key, value, { ...options, event: false }))
        importedCount++
      }
      
      await Promise.all(batchOperations)
      
      // Emit import event
      if (this.eventEmitter) {
        this.eventEmitter.emit('storage:import', {
          size: data.size,
          count: importedCount,
          adapter: this.getName(),
          timestamp: new Date()
        })
      }
      
      console.log(`[IndexedDB] Imported ${importedCount} items from ${data.size} bytes`)
    } catch (error) {
      console.error('[IndexedDB] Failed to import:', error)
      
      // Emit error event
      if (this.eventEmitter) {
        this.eventEmitter.emit('storage:error', {
          error: error as Error,
          operation: 'import',
          adapter: this.getName()
        })
      }
      
      throw error
    }
  }

  async clear(): Promise<void> {
    const db = this.ensureDb()
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['freshcards'], 'readwrite')
      const store = transaction.objectStore('freshcards')
      
      const request = store.clear()
      
      request.onerror = () => {
        const error = new Error('Failed to clear IndexedDB')
        console.error('[IndexedDB] Failed to clear:', error)
        
        // Emit error event
        if (this.eventEmitter) {
          this.eventEmitter.emit('storage:error', {
            error,
            operation: 'clear',
            adapter: this.getName()
          })
        }
        
        reject(error)
      }
      
      request.onsuccess = () => {
        console.log('[IndexedDB] Cleared all data')
        resolve()
      }
    })
  }

  async getKeys(): Promise<string[]> {
    const db = this.ensureDb()
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['freshcards'], 'readonly')
      const store = transaction.objectStore('freshcards')
      
      const request = store.getAllKeys()
      
      request.onerror = () => {
        const error = new Error('Failed to get IndexedDB keys')
        console.error('[IndexedDB] Failed to get keys:', error)
        
        // Emit error event
        if (this.eventEmitter) {
          this.eventEmitter.emit('storage:error', {
            error,
            operation: 'getKeys',
            adapter: this.getName()
          })
        }
        
        reject(error)
      }
      
      request.onsuccess = () => {
        const keys = request.result as string[]
        resolve(keys)
      }
    })
  }

  getName(): string {
    return 'indexeddb'
  }

  async isAvailable(): Promise<boolean> {
    try {
      return 'indexedDB' in window && 
             'IDBDatabase' in window && 
             typeof indexedDB === 'object'
    } catch {
      return false
    }
  }

  async getSize(): Promise<number> {
    const db = this.ensureDb()
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['freshcards'], 'readonly')
      const store = transaction.objectStore('freshcards')
      
      const request = store.getAll()
      
      request.onerror = () => {
        reject(new Error('Failed to get IndexedDB size'))
      }
      
      request.onsuccess = () => {
        const items = request.result
        let totalSize = 0
        
        items.forEach(item => {
          totalSize += JSON.stringify(item.value).length
        })
        
        resolve(totalSize)
      }
    })
  }

  // Close the database connection
  async close(): Promise<void> {
    if (this.db) {
      this.db.close()
      this.db = null
      this.isInitialized = false
      console.log(`[IndexedDB] Database closed: ${this.dbName}`)
    }
  }

  // Get database statistics
  async getStats(): Promise<{ count: number; size: number; oldest: Date | null; newest: Date | null }> {
    const db = this.ensureDb()
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['freshcards'], 'readonly')
      const store = transaction.objectStore('freshcards')
      
      const request = store.getAll()
      
      request.onerror = () => {
        reject(new Error('Failed to get IndexedDB stats'))
      }
      
      request.onsuccess = () => {
        const items = request.result
        let totalSize = 0
        let oldest: Date | null = null
        let newest: Date | null = null
        
        items.forEach(item => {
          totalSize += JSON.stringify(item.value).length
          if (!oldest || item.timestamp < oldest) oldest = item.timestamp
          if (!newest || item.timestamp > newest) newest = item.timestamp
        })
        
        resolve({
          count: items.length,
          size: totalSize,
          oldest,
          newest
        })
      }
    })
  }

  /**
   * F 226 — TRASH. `delete` above is unchanged and still hard.
   * These are the opt-in pair, built on the adapter's own primitives.
   */
  async softDelete(key: string, options: StorageOptions = {}): Promise<void> {
    await softDeleteVia(this as unknown as TrashPrimitives, key, options);
  }

  async restore(key: string, options: StorageOptions = {}): Promise<void> {
    await restoreVia(this as unknown as TrashPrimitives, key, options);
  }

  /** Trashed records under `prefix`, newest first. */
  async listTrash(prefix = '', options: StorageOptions = {}): Promise<unknown[]> {
    return await listTrashVia(this as unknown as TrashPrimitives, prefix, options);
  }
}